import { useLayoutEffect, useRef, useState } from "react";
import { Handshake, MousePointerClick } from "lucide-react";
import { PRODUCT_LABEL, PRODUCT_ORDER, countryName, fmt } from "../lib/constants";
import { initials } from "./OwnerBreakdown";

const GAP = 14;

// Floating preview shown while hovering a country with venues on the map (pointer-events: none, never blocks the click)
export const MapHoverCard = ({ country, pos, bounds, view, showOwners, pending = 0 }) => {
  const ref = useRef(null);
  const [dims, setDims] = useState({ w: 240, h: 150 });
  useLayoutEffect(() => {
    if (ref.current) setDims({ w: ref.current.offsetWidth, h: ref.current.offsetHeight });
  }, [country]);
  if (!country || !pos) return null;

  const products = PRODUCT_ORDER.filter((p) => country.products?.[p]).sort((a, b) => country.products[b] - country.products[a]);
  const left = pos.x + GAP + dims.w > bounds.w ? Math.max(4, pos.x - GAP - dims.w) : pos.x + GAP;
  const top = pos.y + GAP + dims.h > bounds.h ? Math.max(4, pos.y - GAP - dims.h) : pos.y + GAP;

  return (
    <div
      ref={ref}
      data-testid="map-hover-card"
      className="absolute z-20 pointer-events-none w-60 tv:w-[300px] rounded-xl border border-panel-border bg-panel/95 backdrop-blur shadow-lg p-3 tv:p-[16px] text-ink fade-up"
      style={{ left, top }}
    >
      <div className="flex items-start gap-2">
        <span className="num text-[10px] tv:text-[12px] font-bold text-slate-400 mt-1">{country.code}</span>
        <span className="font-bold text-sm tv:text-[17px] leading-tight flex-1 min-w-0 truncate" data-testid="map-hover-card-name">{countryName(country.code)}</span>
        <span className="text-right shrink-0">
          <span className="num block font-black text-2xl tv:text-[30px] leading-none text-brand" data-testid="map-hover-card-venues">{fmt(country.total)}</span>
          <span className="block text-[10px] tv:text-[12px] text-slate-500 -mt-0.5">{country.total === 1 ? "venue" : "venues"}</span>
        </span>
      </div>
      {(country.legacy > 0 || country.partner > 0) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] tv:text-[13px] text-slate-500">
          {country.legacy > 0 && <span>legacy <b className="num text-ink">{fmt(country.legacy)}</b></span>}
          {country.partner > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700" data-testid="map-hover-card-partner">
              <Handshake className="w-3 h-3 tv:w-[14px] tv:h-[14px]" /> via partners <b className="num">{fmt(country.partner)}</b>
            </span>
          )}
        </div>
      )}
      {pending > 0 && (
        <div className="mt-1 text-[11px] tv:text-[13px] font-semibold text-amber-700" data-testid="map-hover-card-pending">
          +{fmt(pending)} not counted · waiting for CRM fix
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-2 tv:mt-[10px]" data-testid="map-hover-card-products">
        {products.length === 0 && <span className="text-[11px] tv:text-[13px] text-slate-400">no products recorded</span>}
        {products.slice(0, 6).map((p) => (
          <span key={p} className={`rounded-full border px-2 py-0.5 text-[11px] tv:text-[13px] font-semibold ${view === p ? "border-brand bg-brand/5 text-brand" : "border-panel-border bg-slate-50 text-slate-700"}`}>
            {PRODUCT_LABEL[p] || p} <b className="num">{fmt(country.products[p])}</b>
          </span>
        ))}
        {products.length > 6 && <span className="text-[11px] tv:text-[13px] text-slate-400 self-center">+{products.length - 6}</span>}
      </div>
      {showOwners && country.top_owner && (
        <div className="flex items-center gap-2 mt-2 tv:mt-[10px] pt-2 tv:pt-[10px] border-t border-panel-border text-xs tv:text-[14px]" data-testid="map-hover-card-owner">
          <span className="w-6 h-6 tv:w-[28px] tv:h-[28px] rounded-full bg-ink/10 text-ink text-[10px] tv:text-[11px] font-bold flex items-center justify-center shrink-0">{initials(country.top_owner)}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] tv:text-[11px] uppercase tracking-wider text-slate-400 font-bold leading-none">Top deal owner</span>
            <span className="block font-semibold truncate">{country.top_owner}</span>
          </span>
          <b className="num shrink-0">{fmt(country.owners?.[0]?.count)}</b>
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-2 tv:mt-[10px] text-[10px] tv:text-[12px] font-semibold text-slate-500">
        <MousePointerClick className="w-3 h-3 tv:w-[14px] tv:h-[14px] text-brand" /> Click for details by day and deal owner
      </div>
    </div>
  );
};
